import React, { useState, useMemo, useCallback, useEffect } from 'react'

import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Paper,
    CircularProgress,
    Checkbox,
    Divider,
    IconButton,
    makeStyles,
    TextField,
    Typography,
    Button,
    MenuItem,
    Menu,
    Popover,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Modal,
    Slide,
    Tooltip,
    useTheme,
} from '@material-ui/core'
import { Theme } from '@material-ui/core/styles'
import { Routes } from '../../../../navigation/config/routes'

import { BulkAddUserForm } from './BulkAddUserForm'
import { FacilitiesPlanModal } from '../../../FacilitiesPlan/FacilitiesPlanModal'

import { toast } from 'react-toastify'

import MoreVertIcon from '@material-ui/icons/MoreVert'
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward'
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward'
import LinkOffIcon from '@material-ui/icons/LinkOff'

import { RowData, HeadCell } from '../../../../components'
import { Address, BaseCompany } from '../../../../models'
import { CompanyWithInvoiceCode } from '../types'
import { axiosInstance, resolveRoute } from '../../../../helpers'
import { usePopover } from '../../../../hooks/usePopover'
import { useDebounce } from '../../../../hooks/useDebounce'
import { useQueryClient } from '@tanstack/react-query'

import {
    useAdminPropertiesList,
    fetchAdminPropertiesBulkSelect,
    useAdminFilterOrganizations,
    useProperties,
    ADMIN_PROPERTIES_LIST_KEY,
} from '../../hooks'
import { usePropertyBulkSelection } from '../hooks/usePropertyBulkSelection'
import {
    mapCompanyToRow,
    SORT_KEY_TO_ORDERING,
    toggleOrdering,
} from '../utils/mapCompanyToRow'
import { SearchHeader } from './SearchHeader'
import { GLOBAL_TOP_BAR_HEIGHT } from '../../../../constants'
import { FOOTER_HEIGHT, SEARCH_HEADER_HEIGHT } from './constants'
import { useHistory } from 'react-router-dom'
import { CompanyStatus } from '../../../../models/Company/types'
import { CopyConfigsModal } from '../../../../components/AdminPanel/CopyConfigsModal'
import { BulkEditPropertiesBilling } from './BulkEditPropertiesBilling'
import { BulkUpdateStatusPopover } from './BulkUpdateStatusPopover'
import {
    EDITABLE_STATUS_OPTIONS,
    formatStatusLabel,
    getStatusDotColor,
} from '../utils/statusUtils'
import { SideDrawer } from '../../../../components/SideDrawer'
import { MessageForm } from '../../../../components/MessageDrawer'
import EditAreaLabelsModal from '../../../../components/AdminPanel/EditAreaLabelsModal'
import { DRAWER_W } from '../../../ActionItem/contants'

type StatusPopoverContext = {
    propertyId: number
    currentStatus: CompanyStatus
}

type StatusPopoverState = {
    anchorPosition: { top: number; left: number }
    context: StatusPopoverContext
}

type PropertyTableHeadCell = HeadCell<RowData> & {
    layout: 'fixed' | 'flex'
}

const getFixedColumnStyle = (
    width?: number,
): React.CSSProperties | undefined => {
    if (!width) {
        return undefined
    }
    return {
        width,
        minWidth: width,
        maxWidth: width,
        boxSizing: 'border-box',
    }
}

const TABLE_HEAD_CELLS: PropertyTableHeadCell[] = [
    {
        id: 'check',
        label: '',
        align: 'left',
        disablePadding: false,
        width: 50,
        layout: 'fixed',
        sortable: false,
    },
    {
        id: 'id',
        label: 'ID',
        align: 'left',
        disablePadding: false,
        width: 70,
        layout: 'fixed',
        sortable: true,
    },
    {
        id: 'name',
        label: 'Name',
        align: 'left',
        disablePadding: false,
        layout: 'flex',
        sortable: true,
    },
    {
        id: 'onboarded_by',
        label: 'Onboarded By',
        align: 'left',
        disablePadding: false,
        width: 160,
        layout: 'fixed',
        sortable: false,
    },
    {
        id: 'address',
        label: 'Address',
        align: 'left',
        disablePadding: false,
        layout: 'flex',
        sortable: true,
    },
    {
        id: 'billing_group_display_name',
        label: 'Quickbooks Name',
        align: 'left',
        disablePadding: false,
        layout: 'flex',
    },
    {
        id: 'billable_units',
        label: 'Units',
        align: 'right',
        disablePadding: false,
        width: 72,
        layout: 'fixed',
        sortable: true,
    },
    {
        id: 'billable_unit_rate',
        label: 'Rate',
        align: 'right',
        disablePadding: false,
        width: 60,
        layout: 'fixed',
        sortable: true,
    },
    {
        id: 'monthly_total',
        label: 'Invoice',
        align: 'right',
        disablePadding: false,
        width: 104,
        layout: 'fixed',
        sortable: false,
    },
    {
        id: 'actions',
        label: 'Actions',
        align: 'left',
        disablePadding: false,
        width: 50,
        layout: 'fixed',
        sortable: false,
    },
]

const getFixedColumnStyleById = (
    id: PropertyTableHeadCell['id'],
): React.CSSProperties | undefined => {
    const cell = TABLE_HEAD_CELLS.find((c) => c.id === id)
    if (!cell || cell.layout !== 'fixed') {
        return undefined
    }
    return getFixedColumnStyle(cell.width)
}

const useLocalStyles = makeStyles((theme) => ({
    contentWrapper: {
        display: 'flex',
        flexDirection: 'column',
        minHeight: `calc(100vh - ${GLOBAL_TOP_BAR_HEIGHT}px)`,
        maxHeight: `calc(100vh - ${GLOBAL_TOP_BAR_HEIGHT}px)`,
        padding: theme.spacing(2),
    },
    searchContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1),
        flex: '0 0 auto',
        minHeight: SEARCH_HEADER_HEIGHT,
        alignItems: 'stretch',
    },
    tableWrapper: {
        flex: 1,
        display: 'flex',
        overflow: 'scroll',
        flexDirection: 'column',
    },

    loadingOverlay: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 1,
    },
    stickyHeader: {
        backgroundColor: theme.palette.background.paper,
    },
    table: {
        tableLayout: 'auto',
        width: 'max-content',
        maxWidth: '100%',
        minWidth: 750,
    },
    flexCell: {
        wordBreak: 'break-word',
        overflowWrap: 'anywhere',
    },
    tableRow: {
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
    },
    footer: {
        display: 'flex',
        justifyContent: 'flex-end',
        minHeight: FOOTER_HEIGHT,
        maxHeight: FOOTER_HEIGHT,
    },
    sortableHeader: {
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
        transition: 'background-color 0.2s ease',
    },
    sortIcon: {
        marginLeft: theme.spacing(0.5),
        fontSize: '1rem',
    },
    bulkEditModal: {
        width: '100%',
        maxWidth: '800px',
        maxHeight: '80vh',
        overflow: 'auto',
    },
    numericCell: {
        fontVariantNumeric: 'tabular-nums',
    },
}))

const formatCurrency = (value: number) => {
    if (value === null || value === undefined || isNaN(value)) {
        return '-'
    }
    const isNumber = typeof value === 'number' && !isNaN(value)
    if (!isNumber) {
        return '-'
    }
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
    }).format(value)
}

const PropertyStatusDot = ({ status }: { status: CompanyStatus }) => {
    const theme = useTheme()
    const color = getStatusDotColor(status, theme)
    const label = formatStatusLabel(status)

    return (
        <Tooltip title={`Status: ${label}`}>
            <span
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: theme.spacing(0.25),
                }}
            >
                <span
                    style={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        backgroundColor: color,
                        flexShrink: 0,
                    }}
                />
            </span>
        </Tooltip>
    )
}

const StatusChangePopover = ({
    popoverState,
    onClose,
    onStatusChange,
}: {
    popoverState: StatusPopoverState | null
    onClose: () => void
    onStatusChange: (id: number, newStatus: CompanyStatus) => void
}) => {
    const theme = useTheme()
    const context = popoverState?.context ?? null

    return (
        <Popover
            open={popoverState !== null}
            anchorReference="anchorPosition"
            anchorPosition={popoverState?.anchorPosition}
            onClose={onClose}
            anchorOrigin={{
                vertical: 'top',
                horizontal: 'left',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
            }}
        >
            <div style={{ padding: theme.spacing(1, 0), minWidth: 160 }}>
                <Typography
                    variant="caption"
                    color="textSecondary"
                    style={{
                        display: 'block',
                        padding: theme.spacing(0, 2, 0.5),
                    }}
                >
                    Change status
                </Typography>
                {EDITABLE_STATUS_OPTIONS.map((option) => (
                    <MenuItem
                        key={option}
                        selected={option === context?.currentStatus}
                        onClick={() => {
                            if (context && option !== context.currentStatus) {
                                onStatusChange(context.propertyId, option)
                            }
                            onClose()
                        }}
                    >
                        <span
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: theme.spacing(1),
                            }}
                        >
                            <span
                                style={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    backgroundColor: getStatusDotColor(
                                        option,
                                        theme,
                                    ),
                                    flexShrink: 0,
                                }}
                            />
                            {formatStatusLabel(option)}
                        </span>
                    </MenuItem>
                ))}
            </div>
        </Popover>
    )
}

export const PropertyList = () => {
    const localClasses = useLocalStyles()

    const history = useHistory()

    const theme = useTheme()

    const queryClient = useQueryClient()

    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(25)

    const [searchState, setSearchState] = useState({
        text: '',
        userEmail: '',
    })
    const debouncedSearch = useDebounce(searchState.text, 400)
    const debouncedUserEmail = useDebounce(searchState.userEmail, 400)

    const [status, setStatus] = useState<CompanyStatus | 'ALL'>(
        CompanyStatus.ACTIVE,
    )
    const [showOnlyDisconnected, setShowOnlyDisconnected] = useState(false)
    const [hasBillingSetup, setHasBillingSetup] = useState(true)
    const [organizationId, setOrganizationId] = useState<number | null>(null)
    const [stateFilter, setStateFilter] = useState<string | null>(null)
    const [ordering, setOrdering] = useState<string>('-id')

    const [dialogState, setDialogState] = useState({
        isOpen: false,
        selectedWorkspace: null as BaseCompany | null,
    })

    const [copyConfigsModalOpen, setCopyConfigsModalOpen] = useState(false)
    const [bulkEditModalOpen, setBulkEditModalOpen] = useState(false)
    const [
        bulkStatusAnchorEl,
        setBulkStatusAnchorEl,
    ] = useState<HTMLElement | null>(null)

    const {
        bulkMode,
        selectedCount,
        toggleBulkMode,
        clearSelection,
        toggleSelection,
        isSelected,
        selectedProperties,
        selectedWorkspaceIds,
        selectAllMatching,
        updateSelectedStatuses,
    } = usePropertyBulkSelection()

    const [isSelectingAllMatching, setIsSelectingAllMatching] = useState(false)

    const {
        organizations: filterOrganizations,
        isLoading: organizationsLoading,
    } = useAdminFilterOrganizations(hasBillingSetup)

    const {
        propertyList: copyConfigsPropertyList,
        isLoading: copyConfigsLoading,
    } = useProperties()

    const filterParams = useMemo(
        () => ({
            search: debouncedSearch,
            organizationId,
            state: stateFilter,
            status,
            userEmail: debouncedUserEmail,
            hasIntegrationError: showOnlyDisconnected,
            hasBillingSetup,
            ordering,
        }),
        [
            debouncedSearch,
            organizationId,
            stateFilter,
            status,
            debouncedUserEmail,
            showOnlyDisconnected,
            hasBillingSetup,
            ordering,
        ],
    )

    const listParams = useMemo(
        () => ({
            ...filterParams,
            page: page + 1,
            pageSize: rowsPerPage,
        }),
        [filterParams, page, rowsPerPage],
    )

    const {
        properties,
        count,
        isLoading,
        isFetching,
        updatePropertyBilling,
        updatePropertyStatus,
        bulkUpdatePropertyStatus,
    } = useAdminPropertiesList(listParams)

    const organizations = useMemo(() => {
        return [...filterOrganizations].sort((a, b) =>
            a.name.localeCompare(b.name),
        )
    }, [filterOrganizations])

    const tableRows = useMemo(() => properties.map(mapCompanyToRow), [
        properties,
    ])

    const visibleHeadCells = useMemo(
        () =>
            bulkMode
                ? TABLE_HEAD_CELLS
                : TABLE_HEAD_CELLS.filter((cell) => cell.id !== 'check'),
        [bulkMode],
    )

    const pagePropertyIds = useMemo(
        () => properties.map((property) => property.id),
        [properties],
    )

    const selectedOnPageCount = useMemo(
        () => pagePropertyIds.filter((id) => isSelected(id)).length,
        [pagePropertyIds, isSelected],
    )

    const allOnPageSelected =
        pagePropertyIds.length > 0 &&
        selectedOnPageCount === pagePropertyIds.length

    const someOnPageSelected = selectedOnPageCount > 0 && !allOnPageSelected

    const handleToggleSelectAllOnPage = useCallback(() => {
        const shouldSelectAll = !allOnPageSelected
        properties.forEach((property) => {
            if (isSelected(property.id) !== shouldSelectAll) {
                toggleSelection(property, shouldSelectAll)
            }
        })
    }, [allOnPageSelected, properties, isSelected, toggleSelection])

    useEffect(() => {
        setPage(0)
    }, [
        debouncedSearch,
        debouncedUserEmail,
        organizationId,
        stateFilter,
        status,
        showOnlyDisconnected,
        hasBillingSetup,
        ordering,
        rowsPerPage,
    ])

    const handleSearchChange = useCallback(
        (field: keyof typeof searchState) => (
            e: React.ChangeEvent<HTMLInputElement>,
        ) => {
            setSearchState((prev) => ({
                ...prev,
                [field]: e.target.value,
            }))
        },
        [],
    )

    const getSortDirectionForColumn = (
        columnId: string | number,
    ): 'asc' | 'desc' | null => {
        const key = String(columnId)
        const base =
            SORT_KEY_TO_ORDERING[key as keyof typeof SORT_KEY_TO_ORDERING] ??
            key
        if (ordering === base) return 'asc'
        if (ordering === `-${base}`) return 'desc'
        return null
    }

    const handleSort = useCallback((property: keyof RowData) => {
        setOrdering((prev) => toggleOrdering(prev, property as string))
    }, [])

    const handleBulkAddUser = useCallback(
        async (email: string) => {
            const workspaceIds = selectedWorkspaceIds()
            try {
                await axiosInstance.post('user/_admin/', {
                    email,
                    workspaces: workspaceIds,
                })
                toast.success(
                    `${email} added to ${workspaceIds.length} workspaces`,
                )
            } catch (e: any) {
                toast.error(`Error: ${e.response?.data || 'Unknown error'}`, {
                    autoClose: 5000,
                })
            }
        },
        [selectedWorkspaceIds],
    )

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage)
    }

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setPage(0)
    }

    const propertyMenuState = usePopover<number>()
    const [
        statusPopoverState,
        setStatusPopoverState,
    ] = useState<StatusPopoverState | null>(null)

    const openStatusPopover = (
        e: React.MouseEvent,
        context: StatusPopoverContext,
    ) => {
        setStatusPopoverState({
            anchorPosition: { top: e.clientY, left: e.clientX },
            context,
        })
    }

    const handleNavigateToDetails = (id: number) => {
        history.push(resolveRoute(Routes.admin.properties.detail, ':id', id))
    }

    const [confirmDialog, setConfirmDialog] = useState({
        open: false,
        propertyId: null as number | null,
    })

    const [facilitiesPlanModal, setFacilitiesPlanModal] = useState({
        open: false,
        company: null as CompanyWithInvoiceCode | null,
    })

    const [companyNameDialog, setCompanyNameDialog] = useState({
        open: false,
        companyId: null as number | null,
        currentName: '',
        newName: '',
    })

    const [channelDrawer, setChannelDrawer] = useState({
        open: false,
        channelId: null as number | null,
    })

    const [editAreaLabelsState, setEditAreaLabelsState] = useState({
        open: false,
        companyId: null as number | null,
    })

    const handleReintegrateWithEntrata = async (id: number) => {
        try {
            await axiosInstance.post(`/company/entrata-reintegrate/`, {
                property_id: id,
            })
            toast.success('Property reintegration beginning...')
            queryClient.invalidateQueries({
                queryKey: [ADMIN_PROPERTIES_LIST_KEY],
            })
        } catch (e: any) {
            toast.error(`Error: ${e.response?.data || 'Unknown error'}`, {
                autoClose: 5000,
            })
        } finally {
            setConfirmDialog({ open: false, propertyId: null })
        }
    }

    const handleStatusUpdate = async (id: number, newStatus: CompanyStatus) => {
        try {
            await updatePropertyStatus(id, newStatus)
            toast.success('Property status updated successfully')
        } catch (e: any) {
            toast.error(
                `Error updating status: ${e.response?.data || 'Unknown error'}`,
                {
                    autoClose: 5000,
                },
            )
        }
    }

    const openReintegrateConfirmation = (id: number) => {
        setConfirmDialog({ open: true, propertyId: id })
        propertyMenuState.handleClose()
    }

    const handleSelectAllMatching = useCallback(async () => {
        if (count === 0) {
            return
        }
        setIsSelectingAllMatching(true)
        try {
            const data = await fetchAdminPropertiesBulkSelect(filterParams)
            selectAllMatching(data.results)
            if (data.truncated) {
                toast.warning(
                    `Selected ${data.selected_count} of ${data.count} matching properties (500 max per selection).`,
                    { autoClose: 6000 },
                )
            } else {
                toast.success(
                    `Selected ${data.selected_count} propert${
                        data.selected_count === 1 ? 'y' : 'ies'
                    } matching current filters.`,
                )
            }
        } catch (e: any) {
            toast.error(
                `Failed to select matching properties: ${
                    e?.response?.data?.detail ||
                    e?.response?.data ||
                    'Unknown error'
                }`,
                { autoClose: 5000 },
            )
        } finally {
            setIsSelectingAllMatching(false)
        }
    }, [count, filterParams, selectAllMatching])

    return (
        <div className={localClasses.contentWrapper}>
            {/* Search Header */}
            <div className={localClasses.searchContainer}>
                <SearchHeader
                    searchState={searchState}
                    onSearchChange={handleSearchChange}
                    showOnlyDisconnected={showOnlyDisconnected}
                    onShowOnlyDisconnectedChange={setShowOnlyDisconnected}
                    hasBillingSetup={hasBillingSetup}
                    onHasBillingSetupChange={setHasBillingSetup}
                    status={status}
                    onStatusChange={setStatus}
                    organizationId={organizationId}
                    onOrganizationChange={setOrganizationId}
                    organizations={organizations}
                    organizationsLoading={organizationsLoading}
                    stateFilter={stateFilter}
                    onStateChange={setStateFilter}
                    bulkMode={bulkMode}
                    onToggleBulkMode={toggleBulkMode}
                    bulkSelectedCount={selectedCount}
                    matchingFilterCount={count}
                    onSelectAllMatching={handleSelectAllMatching}
                    isSelectingAllMatching={isSelectingAllMatching}
                    onBulkAdd={() =>
                        setDialogState((prev) => ({ ...prev, isOpen: true }))
                    }
                    onDeselectAll={clearSelection}
                    onClickCopyConfigs={() => setCopyConfigsModalOpen(true)}
                    onBulkEdit={() => setBulkEditModalOpen(true)}
                    onBulkUpdateStatus={setBulkStatusAnchorEl}
                />
            </div>

            <TableContainer
                component={Paper}
                className={localClasses.tableWrapper}
            >
                <Table className={localClasses.table} size="small" stickyHeader>
                    <colgroup>
                        {visibleHeadCells.map((cell) => (
                            <col
                                key={String(cell.id)}
                                style={
                                    cell.layout === 'fixed'
                                        ? getFixedColumnStyle(cell.width)
                                        : undefined
                                }
                            />
                        ))}
                    </colgroup>
                    <TableHead>
                        <TableRow>
                            {visibleHeadCells.map((cell) => {
                                const isCheckColumn =
                                    bulkMode && cell.id === 'check'

                                return (
                                    <TableCell
                                        key={cell.id}
                                        align={cell.align}
                                        padding={
                                            isCheckColumn
                                                ? 'checkbox'
                                                : cell.disablePadding
                                                ? 'none'
                                                : 'default'
                                        }
                                        className={`${
                                            localClasses.stickyHeader
                                        } ${
                                            cell.sortable
                                                ? localClasses.sortableHeader
                                                : ''
                                        } ${
                                            cell.layout === 'flex'
                                                ? localClasses.flexCell
                                                : ''
                                        }`}
                                        onClick={
                                            cell.sortable
                                                ? () => handleSort(cell.id)
                                                : undefined
                                        }
                                        style={{
                                            ...cell.style,
                                            ...(cell.layout === 'fixed'
                                                ? getFixedColumnStyle(
                                                      cell.width,
                                                  )
                                                : undefined),
                                            cursor: cell.sortable
                                                ? 'pointer'
                                                : 'default',
                                        }}
                                    >
                                        {isCheckColumn ? (
                                            <Checkbox
                                                indeterminate={
                                                    someOnPageSelected
                                                }
                                                checked={allOnPageSelected}
                                                onChange={
                                                    handleToggleSelectAllOnPage
                                                }
                                                color="primary"
                                                onClick={(e) =>
                                                    e.stopPropagation()
                                                }
                                            />
                                        ) : (
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent:
                                                        cell.align === 'right'
                                                            ? 'flex-end'
                                                            : 'flex-start',
                                                }}
                                            >
                                                {cell.label}
                                                {cell.sortable &&
                                                    getSortDirectionForColumn(
                                                        cell.id,
                                                    ) === 'asc' && (
                                                        <ArrowUpwardIcon
                                                            className={
                                                                localClasses.sortIcon
                                                            }
                                                        />
                                                    )}
                                                {cell.sortable &&
                                                    getSortDirectionForColumn(
                                                        cell.id,
                                                    ) === 'desc' && (
                                                        <ArrowDownwardIcon
                                                            className={
                                                                localClasses.sortIcon
                                                            }
                                                        />
                                                    )}
                                            </div>
                                        )}
                                    </TableCell>
                                )
                            })}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {tableRows.map((row) => {
                            const company = row.company.value
                            const billableUnitRate = Number(
                                company.billable_unit_rate,
                            )
                            const billableUnits = company.billable_unit_count
                            const monthlyTotal =
                                billableUnits * billableUnitRate
                            return (
                                <TableRow
                                    key={`ROW_${row.id.value}`}
                                    hover
                                    className={localClasses.tableRow}
                                    onClick={(e) => {
                                        if (bulkMode) {
                                            toggleSelection(
                                                company,
                                                !isSelected(row.id.value),
                                            )
                                            return
                                        }
                                        openStatusPopover(e, {
                                            propertyId: row.id.value,
                                            currentStatus: row.status.value,
                                        })
                                    }}
                                >
                                    {bulkMode && (
                                        <TableCell
                                            padding="checkbox"
                                            style={getFixedColumnStyleById(
                                                'check',
                                            )}
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <Checkbox
                                                checked={isSelected(
                                                    row.id.value,
                                                )}
                                                onChange={(e) =>
                                                    toggleSelection(
                                                        company,
                                                        e.target.checked,
                                                    )
                                                }
                                                color="primary"
                                            />
                                        </TableCell>
                                    )}
                                    <TableCell
                                        style={getFixedColumnStyleById('id')}
                                    >
                                        {row.id.value}
                                    </TableCell>
                                    <TableCell
                                        className={localClasses.flexCell}
                                    >
                                        <PropertyNameCell
                                            name={row.name.value}
                                            organization={
                                                row.organization.value
                                            }
                                            status={row.status.value}
                                            hasIntegrationError={
                                                row.company.value
                                                    ?.has_integration_error ??
                                                false
                                            }
                                        />
                                    </TableCell>
                                    <TableCell
                                        style={getFixedColumnStyleById(
                                            'onboarded_by',
                                        )}
                                    >
                                        <OnboardedByCell
                                            onboardedBy={
                                                company.onboarded_by ?? null
                                            }
                                            onboardedDate={
                                                company.onboarded_date ?? null
                                            }
                                        />
                                    </TableCell>
                                    <TableCell
                                        className={localClasses.flexCell}
                                    >
                                        <AddressCell
                                            address={row.address.value}
                                        />
                                    </TableCell>
                                    <TableCell
                                        className={localClasses.flexCell}
                                    >
                                        {row.billing_group_display_name.value}
                                    </TableCell>
                                    <TableCell
                                        align="right"
                                        className={localClasses.numericCell}
                                        style={getFixedColumnStyleById(
                                            'billable_units',
                                        )}
                                    >
                                        {billableUnits}
                                    </TableCell>
                                    <TableCell
                                        align="right"
                                        className={localClasses.numericCell}
                                        style={getFixedColumnStyleById(
                                            'billable_unit_rate',
                                        )}
                                    >
                                        {isNaN(billableUnitRate)
                                            ? '-'
                                            : billableUnitRate.toFixed(2)}
                                    </TableCell>
                                    <TableCell
                                        align="right"
                                        className={localClasses.numericCell}
                                        style={getFixedColumnStyleById(
                                            'monthly_total',
                                        )}
                                    >
                                        {formatCurrency(monthlyTotal)}
                                    </TableCell>
                                    <TableCell
                                        style={getFixedColumnStyleById(
                                            'actions',
                                        )}
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <IconButton
                                            size="small"
                                            onClick={(e) => {
                                                propertyMenuState.handleOpen(
                                                    e,
                                                    row.id.value,
                                                )
                                            }}
                                        >
                                            <MoreVertIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            )
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            <StatusChangePopover
                popoverState={statusPopoverState}
                onClose={() => setStatusPopoverState(null)}
                onStatusChange={handleStatusUpdate}
            />

            {/* Menu Popover */}
            <Menu
                anchorEl={propertyMenuState.anchorEl}
                open={propertyMenuState.isOpen}
                onClose={propertyMenuState.handleClose}
                getContentAnchorEl={null}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                <MenuItem
                    onClick={() => {
                        handleNavigateToDetails(propertyMenuState.context!)
                        propertyMenuState.handleClose()
                    }}
                >
                    Navigate to Details
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        openReintegrateConfirmation(propertyMenuState.context!)
                    }}
                >
                    Reintegrate with Entrata
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        const selectedRow = tableRows.find(
                            (row) => row.id.value === propertyMenuState.context,
                        )
                        setFacilitiesPlanModal({
                            open: true,
                            company: selectedRow?.company.value || null,
                        })
                        propertyMenuState.handleClose()
                    }}
                >
                    Build Facilities Plan
                </MenuItem>
                <Divider />
                <MenuItem
                    onClick={() => {
                        const selectedRow = tableRows.find(
                            (row) => row.id.value === propertyMenuState.context,
                        )
                        if (selectedRow) {
                            setCompanyNameDialog({
                                open: true,
                                companyId: selectedRow.id.value,
                                currentName: selectedRow.name.value,
                                newName: selectedRow.name.value,
                            })
                        }
                        propertyMenuState.handleClose()
                    }}
                >
                    Change Company Name
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        const selectedRow = tableRows.find(
                            (row) => row.id.value === propertyMenuState.context,
                        )
                        setChannelDrawer({
                            open: true,
                            channelId:
                                selectedRow?.company.value
                                    .account_manager_channel ?? null,
                        })
                        propertyMenuState.handleClose()
                    }}
                >
                    Account Manager Channel
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        setEditAreaLabelsState({
                            open: true,
                            companyId: propertyMenuState.context ?? null,
                        })
                        propertyMenuState.handleClose()
                    }}
                >
                    Edit Area Labels
                </MenuItem>
            </Menu>

            {/* Confirmation Dialog */}
            <Dialog
                open={confirmDialog.open}
                onClose={() =>
                    setConfirmDialog((prev) => ({ ...prev, open: false }))
                }
            >
                <DialogTitle>Confirm Reintegration</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to reintegrate this property with
                        Entrata? This action will refresh all property ID&apos;s
                        from Entrata.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() =>
                            setConfirmDialog((prev) => ({
                                ...prev,
                                open: false,
                            }))
                        }
                        color="primary"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={() =>
                            confirmDialog.propertyId &&
                            handleReintegrateWithEntrata(
                                confirmDialog.propertyId,
                            )
                        }
                        color="primary"
                        variant="contained"
                        autoFocus
                    >
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Facilities Plan Modal */}
            {facilitiesPlanModal.company && (
                <FacilitiesPlanModal
                    open={facilitiesPlanModal.open}
                    onClose={() =>
                        setFacilitiesPlanModal({ open: false, company: null })
                    }
                    company={facilitiesPlanModal.company}
                />
            )}

            {/* Footer */}
            <div className={localClasses.footer}>
                <TablePagination
                    count={count}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onChangePage={handleChangePage}
                    onChangeRowsPerPage={handleChangeRowsPerPage}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                />
            </div>

            {(isLoading || isFetching) && (
                <div className={localClasses.loadingOverlay}>
                    <CircularProgress />
                </div>
            )}

            <BulkAddUserForm
                open={dialogState.isOpen}
                workspaceCount={selectedCount}
                onClose={() =>
                    setDialogState((prev) => ({ ...prev, isOpen: false }))
                }
                onConfirm={handleBulkAddUser}
            />

            <BulkUpdateStatusPopover
                anchorEl={bulkStatusAnchorEl}
                open={bulkStatusAnchorEl !== null}
                onClose={() => setBulkStatusAnchorEl(null)}
                selectedProperties={selectedProperties()}
                onApply={bulkUpdatePropertyStatus}
                onSuccess={updateSelectedStatuses}
            />

            <CopyConfigsModal
                open={copyConfigsModalOpen}
                allApartments={copyConfigsPropertyList?.data ?? []}
                apartmentsLoading={copyConfigsLoading}
                onClose={() => setCopyConfigsModalOpen(false)}
            />

            {/* Change Company Name */}
            <Dialog
                open={companyNameDialog.open}
                onClose={() =>
                    setCompanyNameDialog((prev) => ({ ...prev, open: false }))
                }
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Change Company Name</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        fullWidth
                        label="Company name"
                        variant="outlined"
                        size="small"
                        value={companyNameDialog.newName}
                        onChange={(e) =>
                            setCompanyNameDialog((prev) => ({
                                ...prev,
                                newName: e.target.value,
                            }))
                        }
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                axiosInstance
                                    .post('company/update-company-name/', {
                                        name: companyNameDialog.newName,
                                        company_id: companyNameDialog.companyId,
                                    })
                                    .then(() => {
                                        queryClient.invalidateQueries({
                                            queryKey: [
                                                ADMIN_PROPERTIES_LIST_KEY,
                                            ],
                                        })
                                        toast.success('Company name updated')
                                        setCompanyNameDialog((prev) => ({
                                            ...prev,
                                            open: false,
                                        }))
                                    })
                                    .catch((e) => toast.error(`Failed: ${e}`))
                            }
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() =>
                            setCompanyNameDialog((prev) => ({
                                ...prev,
                                open: false,
                            }))
                        }
                        variant="outlined"
                        size="small"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={() => {
                            axiosInstance
                                .post('company/update-company-name/', {
                                    name: companyNameDialog.newName,
                                    company_id: companyNameDialog.companyId,
                                })
                                .then(() => {
                                    queryClient.invalidateQueries({
                                        queryKey: [ADMIN_PROPERTIES_LIST_KEY],
                                    })
                                    toast.success('Company name updated')
                                    setCompanyNameDialog((prev) => ({
                                        ...prev,
                                        open: false,
                                    }))
                                })
                                .catch((e) => toast.error(`Failed: ${e}`))
                        }}
                        variant="contained"
                        color="primary"
                        size="small"
                        disabled={
                            !companyNameDialog.newName ||
                            companyNameDialog.newName ===
                                companyNameDialog.currentName
                        }
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Account Manager Channel */}
            <SideDrawer
                open={channelDrawer.open}
                width={DRAWER_W}
                handleClose={() =>
                    setChannelDrawer({ open: false, channelId: null })
                }
            >
                {channelDrawer.channelId ? (
                    <MessageForm
                        theme={theme}
                        channelId={channelDrawer.channelId}
                    />
                ) : (
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '100%',
                        }}
                    >
                        <Typography variant="body2" color="textSecondary">
                            No channel configured for this property
                        </Typography>
                    </div>
                )}
            </SideDrawer>

            {/* Edit Area Labels */}
            <EditAreaLabelsModal
                open={editAreaLabelsState.open}
                companyId={editAreaLabelsState.companyId ?? undefined}
                onClose={() =>
                    setEditAreaLabelsState({ open: false, companyId: null })
                }
            />

            <Modal
                open={bulkEditModalOpen}
                onClose={() => setBulkEditModalOpen(false)}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flex: 1,
                }}
            >
                <Slide direction="up" in={bulkEditModalOpen}>
                    <Paper
                        style={{
                            backgroundColor: theme.palette.background.paper,
                        }}
                    >
                        <BulkEditPropertiesBilling
                            propertyList={selectedProperties()}
                            onClose={() => setBulkEditModalOpen(false)}
                            updateProperty={updatePropertyBilling}
                        />
                    </Paper>
                </Slide>
            </Modal>
        </div>
    )
}

const PropertyNameCell = ({
    name,
    organization,
    status,
    hasIntegrationError,
}: {
    name: string
    organization: string
    status: CompanyStatus
    hasIntegrationError: boolean
}) => {
    const theme = useTheme()

    return (
        <span
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: theme.spacing(0.5),
            }}
        >
            <PropertyStatusDot status={status} />
            <div>
                <span
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: theme.spacing(0.5),
                    }}
                >
                    <Typography
                        variant="subtitle1"
                        style={{ fontWeight: 600, lineHeight: 1.3 }}
                    >
                        {name}
                    </Typography>
                    {hasIntegrationError && (
                        <Tooltip title="Integration error – disconnected">
                            <LinkOffIcon
                                style={{
                                    fontSize: 18,
                                    color: theme.palette.error.main,
                                }}
                            />
                        </Tooltip>
                    )}
                </span>
                <Typography variant="subtitle2" color="textSecondary">
                    {organization}
                </Typography>
            </div>
        </span>
    )
}

const OnboardedByCell = ({
    onboardedBy,
    onboardedDate,
}: {
    onboardedBy: string | null
    onboardedDate: string | null
}) => {
    if (!onboardedBy) {
        return (
            <Typography variant="caption" color="textSecondary">
                —
            </Typography>
        )
    }

    const formatted = onboardedDate
        ? new Date(onboardedDate).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
          })
        : null

    return (
        <div>
            <Typography
                variant="subtitle2"
                style={{ fontWeight: 500, lineHeight: 1.3 }}
                noWrap
            >
                {onboardedBy}
            </Typography>
            {formatted && (
                <Typography variant="caption" color="textSecondary">
                    {formatted}
                </Typography>
            )}
        </div>
    )
}

const AddressCell = ({ address }: { address: Address | null }) => {
    if (!address) {
        return <Typography variant="subtitle2">N/A</Typography>
    }

    return (
        <div>
            <Typography variant="subtitle2">{address.address1}</Typography>
            <Typography variant="subtitle2">
                {address.city}, {address.state} {address.zip_code}
            </Typography>
        </div>
    )
}
